import { useEffect, useState } from "react";
import { Cookie } from "lucide-react";
import { Button } from "./ui/button";
import PrivacyPolicyDialog from "./PrivacyPolicyDialog";
import { OPEN_SETTINGS_EVENT, readConsent, saveConsent } from "../lib/cookieConsent";

/**
 * First-layer cookie notice. Shown on every page until the visitor answers
 * (and again a year later, or whenever they click "Configurar cookies").
 * Accepting and rejecting are two equal buttons side by side, as the AEPD
 * requires; "Más información" opens the privacy policy with its cookies
 * section.
 */
export default function CookieConsentBanner() {
  const [open, setOpen] = useState(() => readConsent() === null);
  const [current, setCurrent] = useState(() => readConsent());
  const [policyOpen, setPolicyOpen] = useState(false);

  useEffect(() => {
    const reopen = () => {
      setCurrent(readConsent());
      setOpen(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, reopen);
  }, []);

  const decide = (value) => {
    saveConsent(value);
    setCurrent(value);
    setOpen(false);
  };

  return (
    <>
      {open && (
        <div
          role="region"
          aria-label="Aviso de cookies"
          className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4 pointer-events-none"
          data-testid="cookie-banner"
        >
          <div className="pointer-events-auto max-w-3xl mx-auto bg-white border border-gray-200 rounded-xl shadow-[0_8px_30px_-12px_rgba(0,0,0,0.3)] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="w-5 h-5 text-brand shrink-0 mt-0.5" />
              <div className="text-[13px] leading-relaxed text-gray-600 space-y-1">
                <p>
                  Guardamos en tu navegador lo imprescindible para que la web funcione, como tu
                  sesión. Además, <strong className="text-brand-ink">solo si lo aceptas</strong>,
                  usamos cookies de analítica (PostHog) para saber cómo se usa la web y mejorarla.{" "}
                  <button
                    type="button"
                    onClick={() => setPolicyOpen(true)}
                    className="text-brand-ink underline hover:text-brand"
                    data-testid="cookie-more-info"
                  >
                    Más información
                  </button>
                </p>
                {current && (
                  <p className="text-xs text-gray-500">
                    Ahora mismo las tienes {current === "accepted" ? "aceptadas" : "rechazadas"}.
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                onClick={() => decide("rejected")}
                className="flex-1 sm:flex-none sm:w-28 bg-brand-ink hover:bg-brand-ink/90 text-white"
                data-testid="cookie-reject"
              >
                Rechazar
              </Button>
              <Button
                onClick={() => decide("accepted")}
                className="flex-1 sm:flex-none sm:w-28 bg-brand-ink hover:bg-brand-ink/90 text-white"
                data-testid="cookie-accept"
              >
                Aceptar
              </Button>
            </div>
          </div>
        </div>
      )}
      <PrivacyPolicyDialog open={policyOpen} onOpenChange={setPolicyOpen} />
    </>
  );
}
